import React, { useRef, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import {
    Caption,
    DesktopOnlyLabel,
    DropZone,
    MobileOnlyLabel,
    UploadIcon,
    UploadingRow,
} from './CvUploadDropZone.styles';

export const ACCEPTED_CV_EXTENSIONS = ['.rtf', '.doc', '.docx', '.pdf', '.txt', '.jpg', '.jpeg', '.png'];
export const ACCEPTED_CV_INPUT = ACCEPTED_CV_EXTENSIONS.join(',');
export const MAX_CV_FILE_SIZE_MB = 6;
export const MAX_CV_FILE_SIZE = MAX_CV_FILE_SIZE_MB * 1024 * 1024;

export const validateCvFile = (file: File): string | null => {
    const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
    if (!ACCEPTED_CV_EXTENSIONS.includes(ext)) {
        return `סוג הקובץ "${ext}" אינו נתמך. סוגי קבצים אפשריים: ${ACCEPTED_CV_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_CV_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        return `גודל הקובץ (${sizeMB}MB) חורג מהגודל המקסימלי המותר (${MAX_CV_FILE_SIZE_MB}MB)`;
    }
    return null;
};

export type CvUploadDropZoneProps = {
    disabled?: boolean
    isUploading?: boolean
    selectedFile?: File | null
    disabledLabel?: string
    emptyLabel?: string
    mobileEmptyLabel?: string
    uploadingLabel?: string
    counterText?: string
    onFileSelected: (file: File) => void
    onValidationError?: (message: string) => void
}

const defaultEmptyLabel = 'גרור קובץ לכאן או לחץ לבחירת קובץ';
const defaultUploadingLabel = 'מעלה את הקובץ...';

function CvUploadDropZone({
    disabled = false,
    isUploading = false,
    selectedFile,
    disabledLabel,
    emptyLabel = defaultEmptyLabel,
    mobileEmptyLabel,
    uploadingLabel = defaultUploadingLabel,
    counterText,
    onFileSelected,
    onValidationError,
}: CvUploadDropZoneProps): JSX.Element {
    const [isDragOver, setIsDragOver] = useState(false);
    const fileEl = useRef<HTMLInputElement>(null);

    const isDisabled = disabled || isUploading;

    const processFile = (file?: File) => {
        if (!file || isDisabled) return;

        const validationError = validateCvFile(file);
        if (validationError) {
            onValidationError?.(validationError);
            if (fileEl.current) fileEl.current.value = '';
            return;
        }

        onFileSelected(file);
        if (fileEl.current) fileEl.current.value = '';
    };

    const handleFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        processFile(ev.target.files?.[0]);
    };

    const handleDrop = (ev: React.DragEvent) => {
        ev.preventDefault();
        setIsDragOver(false);
        processFile(ev.dataTransfer.files?.[0]);
    };

    const handleDragOver = (ev: React.DragEvent) => {
        ev.preventDefault();
        if (!isDisabled) setIsDragOver(true);
    };

    const handleDragLeave = () => setIsDragOver(false);

    const label = disabled && disabledLabel
        ? disabledLabel
        : selectedFile?.name || emptyLabel;

    const showResponsiveEmptyLabel = !disabled && !selectedFile && !!mobileEmptyLabel;

    return (
        <DropZone
            $isDragOver={isDragOver}
            $disabled={isDisabled}
            onClick={() => { if (!isDisabled) fileEl.current?.click(); }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            <input
                onChange={handleFileChange}
                hidden
                ref={fileEl}
                type="file"
                accept={ACCEPTED_CV_INPUT}
                aria-describedby="העלאת קובץ"
            />
            {isUploading
                ? <UploadingRow>
                    <CircularProgress size={24} />
                    <Typography color="secondary">{uploadingLabel}</Typography>
                </UploadingRow>
                : <>
                    <UploadIcon $disabled={isDisabled} />
                    <Typography color="secondary" fontWeight="bold" variant="body1">
                        {showResponsiveEmptyLabel
                            ? <>
                                <DesktopOnlyLabel>{emptyLabel}</DesktopOnlyLabel>
                                <MobileOnlyLabel>{mobileEmptyLabel}</MobileOnlyLabel>
                            </>
                            : label}
                    </Typography>
                    <Caption variant="caption" color="text.secondary" display="block">
                        {ACCEPTED_CV_EXTENSIONS.join(', ')} &bull; עד {MAX_CV_FILE_SIZE_MB}MB
                    </Caption>
                    {counterText && <Caption variant="caption" color="text.secondary" display="block">
                        {counterText}
                    </Caption>}
                </>
            }
        </DropZone>
    );
}

export default CvUploadDropZone;
